/**
 * A classroom review board: categories across the top, clues of rising value beneath.
 *
 * The board is typed as plain text so it can be pasted from a document:
 *
 *     # Category name
 *     clue | response
 *     300 | clue with its own value | response
 *
 * A line starting with `#` opens a category. A clue line has a clue and a response
 * separated by a vertical bar; a leading whole number before a third part sets that
 * clue's points, otherwise the points rise by the chosen step down the column.
 */

export const MAX_CATEGORIES = 6;
export const MAX_ROWS = 6;
export const MAX_TEAMS = 6;
export const MAX_TEAM_NAME = 24;
export const POINT_STEPS = [100, 200, 500] as const;
export const DEFAULT_STEP = 100;

const LINE_BREAK = /\r?\n/;
const NAME_SPLIT = /[\n,;]/;
const POINTS = /^\d{1,5}$/;
const HASHES = /^#+/;
const SEPARATOR = "|";

export interface Clue {
  clue: string;
  response: string;
  points: number;
  /** 1-based line in the input. */
  line: number;
}

export interface Category {
  name: string;
  clues: Clue[];
}

export type ProblemKind =
  | "no-category"
  | "empty-clue"
  | "empty-category"
  | "extra-category"
  | "extra-clue";

export interface BoardProblem {
  line: number;
  kind: ProblemKind;
}

export interface ParsedBoard {
  categories: Category[];
  /** The deepest column. */
  rows: number;
  problems: BoardProblem[];
}

interface Draft {
  name: string;
  line: number;
  clues: Clue[];
}

function clueFrom(
  parts: string[],
  index: number,
  step: number,
  line: number
): Clue | null {
  const first = parts[0] ?? "";
  const hasPoints = parts.length >= 3 && POINTS.test(first);
  const body = hasPoints ? parts.slice(1) : parts;
  const clue = (body[0] ?? "").trim();
  if (clue === "") {
    return null;
  }
  return {
    clue,
    response: body.slice(1).join(` ${SEPARATOR} `).trim(),
    points: hasPoints ? Number(first) : (index + 1) * step,
    line,
  };
}

interface Reading {
  drafts: Draft[];
  problems: BoardProblem[];
  current: Draft | null;
  /** True while inside a category beyond the sixth, whose clues are ignored. */
  skipping: boolean;
}

function openCategory(reading: Reading, line: string, number: number): void {
  reading.skipping = reading.drafts.length >= MAX_CATEGORIES;
  if (reading.skipping) {
    reading.problems.push({ line: number, kind: "extra-category" });
    reading.current = null;
    return;
  }
  reading.current = {
    name: line.replace(HASHES, "").trim(),
    line: number,
    clues: [],
  };
  reading.drafts.push(reading.current);
}

function addClue(
  reading: Reading,
  line: string,
  number: number,
  step: number
): void {
  const { current } = reading;
  if (!current) {
    reading.problems.push({ line: number, kind: "no-category" });
    return;
  }
  const clue = clueFrom(
    line.split(SEPARATOR).map((part) => part.trim()),
    current.clues.length,
    step,
    number
  );
  if (!clue) {
    reading.problems.push({ line: number, kind: "empty-clue" });
  } else if (current.clues.length >= MAX_ROWS) {
    reading.problems.push({ line: number, kind: "extra-clue" });
  } else {
    current.clues.push(clue);
  }
}

/** Read the typed board. Lines that cannot be used are listed in `problems`, never thrown. */
export function parseBoard(text: string, step: number): ParsedBoard {
  const reading: Reading = {
    drafts: [],
    problems: [],
    current: null,
    skipping: false,
  };
  for (const [index, raw] of text.split(LINE_BREAK).entries()) {
    const line = raw.trim();
    if (line.startsWith("#")) {
      openCategory(reading, line, index + 1);
    } else if (line !== "" && !reading.skipping) {
      addClue(reading, line, index + 1, step);
    }
  }
  const categories: Category[] = [];
  for (const draft of reading.drafts) {
    if (draft.clues.length === 0) {
      reading.problems.push({ line: draft.line, kind: "empty-category" });
    } else {
      categories.push({
        name: draft.name === "" ? `${categories.length + 1}` : draft.name,
        clues: draft.clues,
      });
    }
  }
  return {
    categories,
    rows: Math.max(0, ...categories.map((category) => category.clues.length)),
    problems: reading.problems.sort((a, b) => a.line - b.line),
  };
}

/** Team names from a comma- or line-separated list; blanks are dropped and long names clipped. */
export function parseTeamNames(text: string): string[] {
  return text
    .split(NAME_SPLIT)
    .map((name) => name.trim().slice(0, MAX_TEAM_NAME))
    .filter((name) => name !== "")
    .slice(0, MAX_TEAMS);
}

/** The teams to score: the names given, topped up to `count` with numbered defaults. */
export function buildTeams(
  count: number,
  names: readonly string[],
  fallback: (n: number) => string
): string[] {
  const size = Math.min(MAX_TEAMS, Math.max(1, Math.round(count)));
  return Array.from({ length: size }, (_, i) => names[i] ?? fallback(i + 1));
}

/** Key for a cell: its column and row. */
export function cellKey(column: number, row: number): string {
  return `${column}-${row}`;
}

/** How many clues the board holds. */
export function clueCount(categories: readonly Category[]): number {
  return categories.reduce((sum, category) => sum + category.clues.length, 0);
}

/** Add (or, if negative, take away) points for one team. An unknown team changes nothing. */
export function awardPoints(
  scores: readonly number[],
  team: number,
  points: number
): number[] {
  return scores.map((score, i) => (i === team ? score + points : score));
}

/** The team or teams on the highest score, or none while everyone is on zero. */
export function leaders(scores: readonly number[]): number[] {
  const top = Math.max(...scores);
  if (scores.length === 0 || top <= 0) {
    return [];
  }
  return scores.flatMap((score, i) => (score === top ? [i] : []));
}
